import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type InventoryLotReturnAllocation,
  InventoryLotReturnAllocationSchema,
  type InventoryLotSaleAllocation,
  InventoryLotSaleAllocationSchema,
  isInventoryLotReturnAllocation,
  isInventoryLotSaleAllocation,
} from "./inventory-lot.js";
import {
  isPurchaseInventoryValuationSnapshot,
  PurchaseInventoryValuationSnapshotSchema,
} from "./purchase-order-receiving.js";
import { allocateMovingAverageOutboundFen } from "./purchase-return.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const SafePositiveIntegerSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const SafeNonNegativeIntegerSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const SaleReturnPhysicalDispositionSchema = Type.Union([
  Type.Literal("SALEABLE_RETURNED"),
  Type.Literal("UNSALEABLE_RETURNED"),
]);
export type SaleReturnPhysicalDisposition = Static<typeof SaleReturnPhysicalDispositionSchema>;

export const SaleReturnInventoryEffectSchema = Type.Union([
  Type.Literal("SALEABLE_STOCK_RESTORED"),
  Type.Literal("NO_SALEABLE_STOCK_CHANGE"),
]);
export type SaleReturnInventoryEffect = Static<typeof SaleReturnInventoryEffectSchema>;

export function saleReturnInventoryEffectForDisposition(
  disposition: SaleReturnPhysicalDisposition,
): SaleReturnInventoryEffect {
  return disposition === "SALEABLE_RETURNED"
    ? "SALEABLE_STOCK_RESTORED"
    : "NO_SALEABLE_STOCK_CHANGE";
}

export const TrackedSaleInventoryCostSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("TRACKED_MOVING_AVERAGE"),
    skuId: StableIdSchema,
    productVersion: SafePositiveIntegerSchema,
    soldBaseQuantity: SafePositiveIntegerSchema,
    inventoryQuantityBefore: SafePositiveIntegerSchema,
    inventoryQuantityAfter: SafeNonNegativeIntegerSchema,
    inventoryVersionBefore: SafePositiveIntegerSchema,
    inventoryVersionAfter: SafePositiveIntegerSchema,
    valuationBefore: PurchaseInventoryValuationSnapshotSchema,
    valuationAfter: PurchaseInventoryValuationSnapshotSchema,
    inventoryValueDecreaseFen: SafeNonNegativeIntegerSchema,
    lotAllocations: Type.Optional(
      Type.Array(InventoryLotSaleAllocationSchema, { minItems: 1, maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type TrackedSaleInventoryCostSnapshot = Static<
  typeof TrackedSaleInventoryCostSnapshotSchema
>;

export const TrackedSaleInventoryReturnSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("TRACKED_MOVING_AVERAGE_RETURN"),
    skuId: StableIdSchema,
    productVersion: SafePositiveIntegerSchema,
    soldBaseQuantity: SafePositiveIntegerSchema,
    returnedQuantityBefore: SafeNonNegativeIntegerSchema,
    returnedQuantityAfter: SafePositiveIntegerSchema,
    returnedBaseQuantity: SafePositiveIntegerSchema,
    inventoryQuantityBefore: SafeNonNegativeIntegerSchema,
    inventoryQuantityAfter: SafePositiveIntegerSchema,
    inventoryVersionBefore: SafePositiveIntegerSchema,
    inventoryVersionAfter: SafePositiveIntegerSchema,
    valuationBefore: PurchaseInventoryValuationSnapshotSchema,
    valuationAfter: PurchaseInventoryValuationSnapshotSchema,
    originalInventoryValueDecreaseFen: SafeNonNegativeIntegerSchema,
    inventoryValueIncreaseFen: SafeNonNegativeIntegerSchema,
    lotAllocations: Type.Optional(
      Type.Array(InventoryLotReturnAllocationSchema, { minItems: 1, maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type TrackedSaleInventoryReturnSnapshot = Static<
  typeof TrackedSaleInventoryReturnSnapshotSchema
>;

function cumulativeTrackedSaleReturnFen(
  originalInventoryValueDecreaseFen: number,
  soldBaseQuantity: number,
  returnedQuantity: number,
): number {
  if (
    !Number.isSafeInteger(originalInventoryValueDecreaseFen) ||
    originalInventoryValueDecreaseFen < 0 ||
    !Number.isSafeInteger(soldBaseQuantity) ||
    soldBaseQuantity < 1 ||
    !Number.isSafeInteger(returnedQuantity) ||
    returnedQuantity < 0 ||
    returnedQuantity > soldBaseQuantity
  ) {
    throw new RangeError("tracked sale return allocation is outside the safe range");
  }
  return Number(
    (BigInt(originalInventoryValueDecreaseFen) * BigInt(returnedQuantity)) /
      BigInt(soldBaseQuantity),
  );
}

/**
 * Allocates the immutable original sale cost across repeated partial returns by cumulative
 * difference. This preserves every cent when the final unit is returned without consulting the
 * current moving-average cost.
 */
export function allocateTrackedSaleReturnFen(
  originalInventoryValueDecreaseFen: number,
  soldBaseQuantity: number,
  returnedQuantityBefore: number,
  returnedQuantityAfter: number,
): number {
  if (
    !Number.isSafeInteger(returnedQuantityBefore) ||
    !Number.isSafeInteger(returnedQuantityAfter) ||
    returnedQuantityBefore < 0 ||
    returnedQuantityAfter <= returnedQuantityBefore ||
    returnedQuantityAfter > soldBaseQuantity
  ) {
    throw new RangeError("tracked sale return quantities are outside the safe range");
  }
  return (
    cumulativeTrackedSaleReturnFen(
      originalInventoryValueDecreaseFen,
      soldBaseQuantity,
      returnedQuantityAfter,
    ) -
    cumulativeTrackedSaleReturnFen(
      originalInventoryValueDecreaseFen,
      soldBaseQuantity,
      returnedQuantityBefore,
    )
  );
}

export function remainingTrackedSaleInventoryValueFen(
  snapshot: TrackedSaleInventoryCostSnapshot,
  returnedQuantity: number,
): number {
  return (
    snapshot.inventoryValueDecreaseFen -
    cumulativeTrackedSaleReturnFen(
      snapshot.inventoryValueDecreaseFen,
      snapshot.soldBaseQuantity,
      returnedQuantity,
    )
  );
}

export function isTrackedSaleInventoryCostSnapshot(
  value: unknown,
): value is TrackedSaleInventoryCostSnapshot {
  if (!Value.Check(TrackedSaleInventoryCostSnapshotSchema, value)) return false;
  const snapshot = value as TrackedSaleInventoryCostSnapshot;
  const lotAllocations = snapshot.lotAllocations as InventoryLotSaleAllocation[] | undefined;
  const lotQuantity = lotAllocations?.reduce(
    (total, allocation) => total + allocation.soldBaseQuantity,
    0,
  );
  if (
    !isPurchaseInventoryValuationSnapshot(snapshot.valuationBefore) ||
    !isPurchaseInventoryValuationSnapshot(snapshot.valuationAfter) ||
    snapshot.soldBaseQuantity > snapshot.inventoryQuantityBefore ||
    snapshot.inventoryQuantityAfter !==
      snapshot.inventoryQuantityBefore - snapshot.soldBaseQuantity ||
    snapshot.inventoryVersionAfter !== snapshot.inventoryVersionBefore + 1 ||
    snapshot.valuationBefore.quantity !== snapshot.inventoryQuantityBefore ||
    snapshot.valuationAfter.quantity !== snapshot.inventoryQuantityAfter ||
    (lotAllocations !== undefined &&
      (new Set(lotAllocations.map((allocation) => allocation.lotId)).size !==
        lotAllocations.length ||
        !Number.isSafeInteger(lotQuantity) ||
        lotQuantity !== snapshot.soldBaseQuantity ||
        !lotAllocations.every(isInventoryLotSaleAllocation)))
  ) {
    return false;
  }
  let expectedDecreaseFen: number;
  try {
    expectedDecreaseFen = allocateMovingAverageOutboundFen(
      snapshot.valuationBefore.totalValueFen,
      snapshot.inventoryQuantityBefore,
      snapshot.soldBaseQuantity,
    );
  } catch {
    return false;
  }
  return (
    snapshot.inventoryValueDecreaseFen === expectedDecreaseFen &&
    snapshot.valuationAfter.totalValueFen ===
      snapshot.valuationBefore.totalValueFen - expectedDecreaseFen
  );
}

export function trackedSaleInventoryCostSnapshotMatchesItem(
  snapshotValue: unknown,
  item: {
    skuId: string;
    productVersion?: number;
    unitCostFen?: number;
    costSnapshotKnown?: boolean;
    quantity: number;
  },
): snapshotValue is TrackedSaleInventoryCostSnapshot {
  return (
    isTrackedSaleInventoryCostSnapshot(snapshotValue) &&
    snapshotValue.skuId === item.skuId &&
    snapshotValue.productVersion === item.productVersion &&
    item.costSnapshotKnown === true &&
    item.unitCostFen === snapshotValue.valuationBefore.averageUnitCost?.floorFen &&
    snapshotValue.soldBaseQuantity === item.quantity
  );
}

export function isTrackedSaleInventoryReturnSnapshot(
  value: unknown,
): value is TrackedSaleInventoryReturnSnapshot {
  if (!Value.Check(TrackedSaleInventoryReturnSnapshotSchema, value)) return false;
  const snapshot = value as TrackedSaleInventoryReturnSnapshot;
  const lotAllocations = snapshot.lotAllocations as InventoryLotReturnAllocation[] | undefined;
  const lotQuantity = lotAllocations?.reduce(
    (total, allocation) => total + allocation.returnedBaseQuantity,
    0,
  );
  if (
    !isPurchaseInventoryValuationSnapshot(snapshot.valuationBefore) ||
    !isPurchaseInventoryValuationSnapshot(snapshot.valuationAfter) ||
    snapshot.returnedQuantityAfter !==
      snapshot.returnedQuantityBefore + snapshot.returnedBaseQuantity ||
    snapshot.returnedQuantityAfter > snapshot.soldBaseQuantity ||
    snapshot.inventoryQuantityAfter !==
      snapshot.inventoryQuantityBefore + snapshot.returnedBaseQuantity ||
    snapshot.inventoryVersionAfter !== snapshot.inventoryVersionBefore + 1 ||
    snapshot.valuationBefore.quantity !== snapshot.inventoryQuantityBefore ||
    snapshot.valuationAfter.quantity !== snapshot.inventoryQuantityAfter ||
    (lotAllocations !== undefined &&
      (new Set(lotAllocations.map((allocation) => allocation.lotId)).size !==
        lotAllocations.length ||
        !Number.isSafeInteger(lotQuantity) ||
        lotQuantity !== snapshot.returnedBaseQuantity ||
        !lotAllocations.every(isInventoryLotReturnAllocation)))
  ) {
    return false;
  }
  let expectedIncreaseFen: number;
  try {
    expectedIncreaseFen = allocateTrackedSaleReturnFen(
      snapshot.originalInventoryValueDecreaseFen,
      snapshot.soldBaseQuantity,
      snapshot.returnedQuantityBefore,
      snapshot.returnedQuantityAfter,
    );
  } catch {
    return false;
  }
  return (
    snapshot.inventoryValueIncreaseFen === expectedIncreaseFen &&
    snapshot.valuationAfter.totalValueFen ===
      snapshot.valuationBefore.totalValueFen + expectedIncreaseFen
  );
}

export function trackedSaleInventoryReturnSnapshotMatchesOriginalSale(
  returnSnapshotValue: unknown,
  originalSaleSnapshotValue: unknown,
): returnSnapshotValue is TrackedSaleInventoryReturnSnapshot {
  if (
    !(
      isTrackedSaleInventoryReturnSnapshot(returnSnapshotValue) &&
      isTrackedSaleInventoryCostSnapshot(originalSaleSnapshotValue) &&
      returnSnapshotValue.skuId === originalSaleSnapshotValue.skuId &&
      returnSnapshotValue.productVersion === originalSaleSnapshotValue.productVersion &&
      returnSnapshotValue.soldBaseQuantity === originalSaleSnapshotValue.soldBaseQuantity &&
      returnSnapshotValue.originalInventoryValueDecreaseFen ===
        originalSaleSnapshotValue.inventoryValueDecreaseFen
    )
  ) {
    return false;
  }
  const originalLots = originalSaleSnapshotValue.lotAllocations as
    | InventoryLotSaleAllocation[]
    | undefined;
  const returnedLots = returnSnapshotValue.lotAllocations as
    | InventoryLotReturnAllocation[]
    | undefined;
  if (originalLots === undefined) return returnedLots === undefined;
  if (returnedLots === undefined) return false;
  let cursor = 0;
  const expectedLots = originalLots.flatMap((original) => {
    const segmentStart = cursor;
    const segmentEnd = cursor + original.soldBaseQuantity;
    cursor = segmentEnd;
    const overlapStart = Math.max(segmentStart, returnSnapshotValue.returnedQuantityBefore);
    const overlapEnd = Math.min(segmentEnd, returnSnapshotValue.returnedQuantityAfter);
    const returnedBaseQuantity = Math.max(0, overlapEnd - overlapStart);
    return returnedBaseQuantity === 0
      ? []
      : [
          {
            lotId: original.lotId,
            lotNumber: original.lotNumber,
            expiryDate: original.expiryDate,
            returnedBaseQuantity,
          },
        ];
  });
  return (
    expectedLots.length === returnedLots.length &&
    expectedLots.every((expected, index) => {
      const actual = returnedLots[index];
      return (
        actual !== undefined &&
        actual.lotId === expected.lotId &&
        actual.lotNumber === expected.lotNumber &&
        actual.expiryDate === expected.expiryDate &&
        actual.returnedBaseQuantity === expected.returnedBaseQuantity
      );
    })
  );
}
